import ListCard from "./ListCard";
import ItemCard, { ItemContentData } from "./ItemCard";
import { SourceType } from "@/constants";
import styles from "./index.module.scss";
import MoreFuctions from "./MoreFunctions";
import Carousel, { AttachType } from "../Carousel";
import StickySvg from "../../assets/icon/recommand/sticky.svg";
import React, {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from "react";
import {
  CommonComponent,
  IHotThread,
  ISubChannelTreeRes,
  TopLevelChannels,
} from "@/api/home";
import { StoreContext, namespace } from "@/context";

const regex = /equipment-circle/;

// 页面Header的固定高度 64px + 固定边距 16px
const ContentOffsetTop = 80;

interface BulletinData {
  tid?: number;
  title: string;
}

const supportHotListChannels: TopLevelChannels[] = [
  TopLevelChannels.home_page_recommended_channel,
  TopLevelChannels.home_page_plaza_channel,
];


const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const RecommandArea: FC<{
  source: ISubChannelTreeRes;
  hotList: IHotThread[];
}> = ({ source, hotList = [] }) => {
  const { state } = useContext(StoreContext);
  const { track, TRACK_EVENTS } = state[namespace.app].sensors || {};

  const containerRef = useRef<HTMLDivElement | null>(null);
  const nextElementRef = useRef<HTMLElement | null>(null);

  const [preRolls, setPreRolls] = useState<CommonComponent>();
  const [quickEntry, setQuickEntry] = useState<CommonComponent>();
  const [recommandThreads, setRecommandThreads] = useState<CommonComponent>();

  useEffect(() => {
    if (source) {
      (source.channel_config?.common_component_list || []).forEach(
        (listItem) => {
          if (listItem.type === SourceType.RecommendThreads) {
            setRecommandThreads(listItem);
          }
          if (listItem.type === SourceType.PreRolls) {
            setPreRolls(listItem);
          }
          if (listItem.type === SourceType.QuickImport) {
            setQuickEntry(listItem);
          }
        }
      );
    }
  }, [source]);

  useEffect(() => {
    const calculateStickyPosition = () => {
      nextElementRef.current = document.querySelectorAll(
        "#__next"
      )?.[0] as HTMLElement | null;
      if (containerRef.current && nextElementRef.current) {
        const container = containerRef.current;
        const childElements = Array.from(container.children) as HTMLElement[];

        // 计算滚动到最后一个子组件完全进入视窗所需的滚动高度
        const lastElement = childElements[childElements.length - 1];
        if (
          (lastElement?.offsetTop ?? 0) +
            ContentOffsetTop +
            (lastElement?.offsetHeight ?? 0) -
            window.innerHeight <
          0
        ) {
          containerRef.current!.parentNode!.parentElement!.style.position =
            "sticky";
          containerRef.current!.parentNode!.parentElement!.style.top = "80px";
          return;
        }
        const scrollHeightToLastElement = Math.abs(
          lastElement.offsetTop +
            ContentOffsetTop +
            lastElement.offsetHeight -
            window.innerHeight
        );

        // 计算在最后一个子组件完全进入视窗时，哪些子组件仍然完全可见
        const fullyVisibleIndex = childElements.findIndex((child) => {
          // 减去滚动的距离之后大于等于header固定高度标识该模块还在视窗内
          const childOffSetTop = child.offsetTop + ContentOffsetTop;
          return childOffSetTop - scrollHeightToLastElement >= ContentOffsetTop;
        });
        // 通过吸顶模块计算出父容器吸顶的top
        const stickTop =
          childElements[fullyVisibleIndex]?.offsetTop - ContentOffsetTop;
        containerRef.current!.parentNode!.parentElement!.style.position =
          "sticky";
        containerRef.current!.parentNode!.parentElement!.style.top = `-${stickTop}px`;
      }
    };
    calculateStickyPosition();
    const handleResize = debounce(() => {
      calculateStickyPosition();
    }, 300);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [source, hotList, recommandThreads, preRolls, quickEntry]);

  const onBannerTrackCb = (order: number, link: string) => {
    track?.(
      TRACK_EVENTS?.[namespace.home].materialClick.name,
      {
        list_name: "首页",
        banner_order: order,
        banner_url: link,
        position: "Bottom",
      },
      namespace.home
    );
  };


  const renderArea = useMemo(() => {
    const sortList = (source?.channel_config?.common_component_list || []).sort(
      (a, b) => b.weight - a.weight
    );
    return (sortList || []).map((listItem) => {
      // 贴片广告 且 存在有效数据
      if (
        listItem.type === SourceType.PreRolls &&
        listItem.external_info_list!.some((i) => i.is_valid)
      ) {
        return (
          <Carousel
            width={320}
            height={160}
            key="carousel"
            items={(listItem?.external_info_list || [])
              .filter((i) => i.is_valid)
              .map((item) => ({
                alt: "",
                src: item.pc_url,
                link: {
                  src: item.link_url,
                  openType: item.open_type,
                },
                type: AttachType.Image,
              }))}
            onBannerTrackCb={onBannerTrackCb}
          />
        );
      }
      // 快捷入口 且 存在有效数据
      if (
        listItem.type === SourceType.QuickImport &&
        listItem.external_info_list!.some((i) => i.is_valid)
      ) {
        return (
          <MoreFuctions
            key="quickEntry"
            title={listItem?.title ?? ""}
            data={(listItem?.external_info_list || [])
              .filter((i) => i.is_valid)
              .map((item) => ({
                title: item.title || "",
                icon: item.pc_url,
                link: item.link_url,
                openType: item.open_type,
              }))}
          />
        );
      }
      // 帖子推荐 且 数据不为空
      if (
        listItem.type === SourceType.RecommendThreads &&
        listItem.simple_thread_list!.length
      ) {
        return (
          <ListCard<BulletinData>
            key="recommendThreads"
            width={280}
            defaultStyle={false}
            title={listItem?.title}
            position="OfficialAnnouncement"
            data={(listItem?.simple_thread_list || []).map((item, index) => ({
              tid: item.tid,
              title: item.subject,
              num:
                index <= 1 ? (
                  <div className={styles.stickyIcon}>
                    <StickySvg />
                  </div>
                ) : (
                  index - 1
                ),
            }))}
          />
        );
      }
      // 圈子推荐且数据不为空
      if (
        listItem.type === SourceType.RecommendCircles &&
        listItem.simple_channel_list!.length
      ) {
        return (
          <ItemCard
            title={listItem?.title}
            width={280}
            key="recommendCircles"
            data={ listItem.simple_channel_list || []}
            position=""
          />
        );
      }
    });
  }, [source, track]);

  return (
    <div ref={containerRef} className={styles.recommandContent}>
      {!!hotList.length &&
        supportHotListChannels.includes(source.slug as TopLevelChannels) && (
          <ListCard<IHotThread>
            title="社区热点"
            width={280}
            data={hotList}
            position="HotPosts"
          />
        )}
      {renderArea}
    </div>
  );
};

export default RecommandArea;
